import React, { useState, useEffect, useRef } from 'react';
import { UseFormRegister, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface AllowanceInSwedenFormProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  isTaxFree: string;
  selectedFiles: File[];
  tripStartDate: string;
  tripEndDate: string;
  travelDays: number;
  allowancePerDay: string;
  total: string;
  fileError?: string;
  onFormChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  formData?: any;
  vatFromPreferences?: number;
}

const AllowanceInSwedenForm: React.FC<AllowanceInSwedenFormProps> = ({
  register,
  errors,
  trigger,
  isTaxFree,
  selectedFiles,
  tripStartDate,
  tripEndDate,
  travelDays,
  allowancePerDay,
  total,
  fileError,
  onFormChange,
  onFileUpload,
  onRemoveFile,
  formData,
  vatFromPreferences
}) => {
  const { t } = useTranslation();
  
  // Dropdown states
  const [isTaxFreeDropdownOpen, setIsTaxFreeDropdownOpen] = useState(false);
  const [showOnInvoiceDropdownOpen, setShowOnInvoiceDropdownOpen] = useState(false);
  const [isPdfAttachedDropdownOpen, setIsPdfAttachedDropdownOpen] = useState(false);
  const [isPartialWorkdayDropdownOpen, setIsPartialWorkdayDropdownOpen] = useState(false);

  // Refs for click outside
  const taxFreeDropdownRef = useRef<HTMLDivElement>(null);
  const showOnInvoiceDropdownRef = useRef<HTMLDivElement>(null);
  const isPdfAttachedDropdownRef = useRef<HTMLDivElement>(null);
  const isPartialWorkdayDropdownRef = useRef<HTMLDivElement>(null);

  // Options
  const taxFreeOptions = [
    { value: '', label: t('SELECT_OPTION') },
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  // Helper functions
  const getTaxFreeLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_OPTION');
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getYesNoLabel = (value: string) => {
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getPartialWorkdayLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_OPTION');
    return value === 'yes' ? t('YES') : t('NO');
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (taxFreeDropdownRef.current && !taxFreeDropdownRef.current.contains(event.target as Node)) {
        setIsTaxFreeDropdownOpen(false);
      }
      if (showOnInvoiceDropdownRef.current && !showOnInvoiceDropdownRef.current.contains(event.target as Node)) {
        setShowOnInvoiceDropdownOpen(false);
      }
      if (isPdfAttachedDropdownRef.current && !isPdfAttachedDropdownRef.current.contains(event.target as Node)) {
        setIsPdfAttachedDropdownOpen(false);
      }
      if (isPartialWorkdayDropdownRef.current && !isPartialWorkdayDropdownRef.current.contains(event.target as Node)) {
        setIsPartialWorkdayDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Convert datetime string to date format for HTML input
  const formatDateForInput = (dateStr: string) => {
    if (!dateStr) return '';
    // Handle ISO format "2025-12-02T00:00:00" or already formatted "2025-12-02"
    return dateStr.split('T')[0];
  };

  const validateEndDate = (endDate: string) => {
    if (!tripStartDate || !endDate) return true;
    return new Date(endDate) >= new Date(tripStartDate) || t('END_DATE_AFTER_START');
  };

  return (
    <>
      {/* Row 1: Is Tax Free & Show on Invoice */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('IS_ALLOWANCE_SWEDEN_TAX_FREE')}</label>
          <div ref={taxFreeDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isTaxFree', { required: t('PLEASE_SELECT_OPTION') })} />
            <div
              onClick={() => setIsTaxFreeDropdownOpen(!isTaxFreeDropdownOpen)}
              style={{
                height: '46px',
                border: errors.isTaxFree ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!isTaxFree || isTaxFree === '') ? '#68727d' : '#252525',
                boxShadow: isTaxFreeDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isTaxFreeDropdownOpen ? '1px solid #6750b8' : 'none'
              }}
            >
              <span>{getTaxFreeLabel(isTaxFree)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isTaxFreeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isTaxFreeDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {taxFreeOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isTaxFree', option.value);
                      trigger('isTaxFree');
                      setIsTaxFreeDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: isTaxFree === option.value ? '#f4f3f9' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4f3f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.isTaxFree && (
            <div className="error-message">{errors.isTaxFree.message?.toString()}</div>
          )}
        </div>

        {/* Show on Invoice - Only if isTaxFree is NOT 'no' */}
        {isTaxFree !== 'no' && (
          <div className="form-group">
            <label>{t('SHOW_ON_INVOICE_QUESTION')}</label>
            <div ref={showOnInvoiceDropdownRef} style={{ position: 'relative' }}>
              <input type="hidden" {...register('showOnInvoice')} />
              <div
                onClick={() => setShowOnInvoiceDropdownOpen(!showOnInvoiceDropdownOpen)}
                style={{
                  height: '46px',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  boxShadow: showOnInvoiceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                  outline: showOnInvoiceDropdownOpen ? '1px solid #6750b8' : 'none'
                }}
              >
                <span>{getYesNoLabel(formData?.showOnInvoice || 'yes')}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    transform: showOnInvoiceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {showOnInvoiceDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#fff',
                    border: '1px solid #eaebf0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    overflow: 'hidden',
                    padding: '4px 0'
                  }}
                >
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onFormChange('showOnInvoice', option.value);
                        trigger('showOnInvoice');
                        setShowOnInvoiceDropdownOpen(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        margin: '0 8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#252525',
                        backgroundColor: (formData?.showOnInvoice || 'yes') === option.value ? '#f4f3f9' : '#fff',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#f4f3f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload File (PDF only) - Full Width */}
      <div className="form-group">
        <label>{t('UPLOAD_FILE_PDF_1MB')}</label>
        <div className="file-upload-wrapper">
          <div 
            className="file-upload-area"
            onClick={() => document.getElementById('file-upload-allowance-in-pdf')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <button 
              type="button" 
              className="browse-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedFiles.length > 0 ? t('CHANGE_PDF_FILE') : t('UPLOAD_PDF_FILE')}
            </button>
            <input 
              type="file" 
              id="file-upload-allowance-in-pdf" 
              accept=".pdf,application/pdf" 
              onChange={onFileUpload} 
              className="file-input"
              style={{ display: 'none' }}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="uploaded-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-tag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{file.name}</span>
                  <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                    ({(file.size / 1024).toFixed(2)} {t('KB')})
                  </span>
                  <button type="button" className="remove-btn" onClick={() => onRemoveFile(index)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {fileError && (
          <div className="error-message" style={{ marginTop: '8px' }}>{fileError}</div>
        )}
      </div>

      {/* Show PDF to Customer - Always Visible */}
      {selectedFiles.length > 0 && (
        <div className="form-group">
          <label>{t('SHOW_PDF_TO_CUSTOMER')}</label>
          <div ref={isPdfAttachedDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isPdfAttachedToInvoice')} />
            <div
              onClick={() => setIsPdfAttachedDropdownOpen(!isPdfAttachedDropdownOpen)}
              style={{
                height: '46px',
                border: '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: '#252525',
                boxShadow: isPdfAttachedDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isPdfAttachedDropdownOpen ? '1px solid #6750b8' : 'none'
              }}
            >
              <span>{getYesNoLabel(formData?.isPdfAttachedToInvoice || 'yes')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isPdfAttachedDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isPdfAttachedDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {yesNoOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isPdfAttachedToInvoice', option.value);
                      trigger('isPdfAttachedToInvoice');
                      setIsPdfAttachedDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: (formData?.isPdfAttachedToInvoice || 'yes') === option.value ? '#f4f3f9' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4f3f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Partial Workday & Trip Started Date */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('PARTIAL_WORKDAY_QUESTION')}</label>
          <div ref={isPartialWorkdayDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isPartialWorkday', { required: t('PLEASE_SELECT_OPTION') })} />
            <div
              onClick={() => setIsPartialWorkdayDropdownOpen(!isPartialWorkdayDropdownOpen)}
              style={{
                height: '46px',
                border: errors.isPartialWorkday ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!formData?.isPartialWorkday || formData?.isPartialWorkday === '') ? '#68727d' : '#252525',
                boxShadow: isPartialWorkdayDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isPartialWorkdayDropdownOpen ? '1px solid #6750b8' : 'none'
              }}
            >
              <span>{getPartialWorkdayLabel(formData?.isPartialWorkday || '')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isPartialWorkdayDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isPartialWorkdayDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {taxFreeOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isPartialWorkday', option.value);
                      trigger('isPartialWorkday');
                      setIsPartialWorkdayDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: formData?.isPartialWorkday === option.value ? '#f4f3f9' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (formData?.isPartialWorkday !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4f3f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData?.isPartialWorkday !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.isPartialWorkday && (
            <div className="error-message">{errors.isPartialWorkday.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('TRIP_STARTED_DATE')}</label>
          <input
            type="date"
            {...register('tripStartDate', { required: t('PLEASE_SELECT_START_DATE') })}
            className={`form-control ${errors.tripStartDate ? 'error' : ''}`}
            value={formatDateForInput(formData?.tripStartDate || tripStartDate)}
            onChange={(e) => {
              onFormChange('tripStartDate', e.target.value);
              trigger('tripStartDate');
            }}
          />
          {errors.tripStartDate && (
            <div className="error-message">{errors.tripStartDate.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 3: Trip Ended Date & Reason For Trip */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('TRIP_ENDED_DATE')}</label>
          <input
            type="date"
            {...register('tripEndDate', { 
              required: t('PLEASE_SELECT_END_DATE'),
              validate: validateEndDate
            })}
            className={`form-control ${errors.tripEndDate ? 'error' : ''}`}
            value={formatDateForInput(formData?.tripEndDate || tripEndDate)}
            onChange={(e) => {
              onFormChange('tripEndDate', e.target.value);
              trigger('tripEndDate');
            }}
          />
          {errors.tripEndDate && (
            <div className="error-message">{errors.tripEndDate.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('REASON_FOR_TRIP')}</label>
          <input
            type="text"
            {...register('reason', { required: t('PLEASE_ENTER_REASON') })}
            className={`form-control ${errors.reason ? 'error' : ''}`}
            placeholder={t('WRITE_HERE')}
            onChange={(e) => {
              onFormChange('reason', e.target.value);
              trigger('reason');
            }}
          />
          {errors.reason && (
            <div className="error-message">{errors.reason.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 4: Country & Destination */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('COUNTRY')}</label>
          <input
            type="text"
            {...register('country')}
            className="form-control"
            value={t('SWEDEN')}
            disabled
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>{t('DESTINATION')}</label>
          <input
            type="text"
            {...register('destination', { required: t('PLEASE_ENTER_DESTINATION') })}
            className={`form-control ${errors.destination ? 'error' : ''}`}
            placeholder={t('WRITE_HERE')}
            onChange={(e) => {
              onFormChange('destination', e.target.value);
              trigger('destination');
            }}
          />
          {errors.destination && (
            <div className="error-message">{errors.destination.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Summary Section */}
      <div className="summary-section">
        <h6>{t('SUMMARY')}</h6>
        <div className="form-row">
          <div className="form-group">
            <label>{t('TRAVEL_DAYS')}:</label>
            <input
              type="text"
              className="form-control"
              value={travelDays}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>

          <div className="form-group">
            <label>{t('ALLOWANCE_PER_DAY')}:</label>
            <input
              type="text"
              className="form-control"
              value={`${allowancePerDay} ${t('SEK')}`}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>{t('TOTAL')}:</label>
            <input
              type="text"
              className="form-control"
              value={`${total} ${t('SEK')}`}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        </div>

        {/* VAT Info Display */}
        {vatFromPreferences !== undefined && (
          <div className="form-group">
            <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '14px', color: '#666', marginTop: '12px' }}>
              <strong>{t('VAT')}:</strong> {vatFromPreferences}% {t('FROM_INVOICE_PREFERENCES') || '(from invoice preferences)'}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AllowanceInSwedenForm;
